import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Upload, 
  File, 
  CheckCircle, 
  AlertCircle, 
  X, 
  Play,
  Users,
  Globe,
  Eye,
  EyeOff
} from 'lucide-react';
import { usePlaylistUpload } from '@/hooks/usePlaylistUpload';
import { cn } from '@/utils/functions/cn';

const uploadSchema = z.object({
  name: z.string()
    .min(1, 'Nome é obrigatório')
    .max(100, 'Nome muito longo'),
  description: z.string()
    .max(500, 'Descrição muito longa')
    .optional(),
  isPublic: z.boolean().default(false),
  tags: z.string().optional()
});

type UploadFormData = z.infer<typeof uploadSchema>;

export const PlaylistUpload = () => {
  const { 
    selectedFile, 
    preview, 
    uploadProgress, 
    isUploading, 
    selectFile, 
    uploadPlaylistFile, 
    clearFile,
    uploadData 
  } = usePlaylistUpload();

  const [showPreview, setShowPreview] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue
  } = useForm<UploadFormData>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      isPublic: false
    }
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      selectFile(acceptedFiles[0]);
      setValue('name', acceptedFiles[0].name.replace(/\.(m3u|m3u8)$/i, ''));
    }
  }, [selectFile, setValue]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.apple.mpegurl': ['.m3u'],
      'application/x-mpegURL': ['.m3u8'],
      'text/plain': ['.m3u', '.m3u8']
    },
    maxFiles: 1,
    disabled: isUploading
  });

  const onSubmit = (data: UploadFormData) => {
    const tags = data.tags ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [];
    
    uploadPlaylistFile({
      name: data.name,
      description: data.description,
      isPublic: data.isPublic,
      tags
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Show success state
  if (uploadProgress?.stage === 'complete' && uploadData?.success) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-600 dark:text-green-400">
            Upload Concluído!
          </CardTitle>
          <CardDescription>
            Sua playlist foi enviada com sucesso
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {uploadData.data && (
            <div className="space-y-3 p-4 bg-muted rounded-lg">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nome:</span>
                <span className="font-medium">{uploadData.data.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Canais:</span>
                <span className="font-medium">{uploadData.data.channelCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Enviado em:</span>
                <span className="font-medium">
                  {new Date(uploadData.data.uploadedAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={clearFile} variant="outline" className="flex-1">
              Enviar Outra Playlist
            </Button>
            <Button className="flex-1">
              Ver Playlist
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      {/* File Upload Area */}
      {!selectedFile && (
        <Card>
          <CardHeader className="text-left">
            <CardTitle className="flex items-center gap-2 text-left">
              <Upload className="h-5 w-5" />
              Upload de Playlist
            </CardTitle>
            <CardDescription className="text-left">
              Envie seus arquivos M3U ou M3U8 para começar a assistir
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25",
                "hover:border-primary hover:bg-primary/5"
              )}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              {isDragActive ? (
                <p className="text-lg font-medium">Solte o arquivo aqui...</p>
              ) : (
                <div>
                  <p className="text-lg font-medium mb-2">
                    Arraste e solte seu arquivo M3U/M3U8 aqui
                  </p>
                  <p className="text-muted-foreground mb-4">
                    ou clique para selecionar
                  </p>
                  <Button variant="outline">
                    Selecionar Arquivo
                  </Button>
                </div>
              )}
            </div>
            
            <div className="mt-4 text-sm text-muted-foreground">
              <p><strong>Formatos suportados:</strong> .m3u, .m3u8</p>
              <p><strong>Tamanho máximo:</strong> 50MB</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* File Selected */}
      {selectedFile && !uploadProgress && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* File Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <File className="h-5 w-5" />
                  Arquivo Selecionado
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={clearFile}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nome:</span>
                  <span className="font-medium">{selectedFile.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tamanho:</span>
                  <span className="font-medium">{formatFileSize(selectedFile.size)}</span>
                </div>
                {preview && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Canais:</span>
                    <span className="font-medium">{preview.info.totalChannels}</span>
                  </div>
                )}
              </div>

              {/* Validation Results */}
              {preview?.validation && (
                <div className="space-y-2">
                  {preview.validation.errors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          {preview.validation.errors.map((error, index) => (
                            <div key={index}>• {error}</div>
                          ))}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {preview.validation.warnings.length > 0 && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          {preview.validation.warnings.map((warning, index) => (
                            <div key={index}>• {warning}</div>
                          ))}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {/* Preview Toggle */}
              {preview && (
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(!showPreview)}
                  className="w-full"
                >
                  {showPreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                  {showPreview ? 'Ocultar' : 'Mostrar'} Preview
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Upload Form */}
          <Card>
            <CardHeader className="text-left">
              <CardTitle className="text-left">Informações da Playlist</CardTitle>
              <CardDescription className="text-left">
                Configure os detalhes da sua playlist
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome da Playlist</Label>
                  <Input
                    id="name"
                    placeholder="Minha Playlist"
                    {...register('name')}
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição (opcional)</Label>
                  <Textarea
                    id="description"
                    placeholder="Descrição da playlist..."
                    rows={3}
                    {...register('description')}
                    className={errors.description ? "border-red-500" : ""}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-500">{errors.description.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (opcional)</Label>
                  <Input
                    id="tags"
                    placeholder="esportes, filmes, séries"
                    {...register('tags')}
                  />
                  <p className="text-xs text-muted-foreground">
                    Separe as tags com vírgulas
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isPublic"
                    {...register('isPublic')}
                    className="rounded"
                  />
                  <Label htmlFor="isPublic" className="text-sm">
                    Tornar playlist pública
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={!isValid || !preview?.validation.isValid}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Enviar Playlist
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Upload Progress */}
      {uploadProgress && (
        <Card>
          <CardHeader>
            <CardTitle>Enviando Playlist</CardTitle>
            <CardDescription>
              {uploadProgress.message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Progress value={uploadProgress.percentage} className="w-full" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{uploadProgress.stage}</span>
                <span>{uploadProgress.percentage}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {showPreview && preview && (
        <Card>
          <CardHeader>
            <CardTitle>Preview da Playlist</CardTitle>
            <CardDescription>
              Primeiros canais encontrados no arquivo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <Play className="h-6 w-6 mx-auto mb-1 text-primary" />
                  <div className="text-2xl font-bold">{preview.info.totalChannels}</div>
                  <div className="text-xs text-muted-foreground">Canais</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <Users className="h-6 w-6 mx-auto mb-1 text-primary" />
                  <div className="text-2xl font-bold">{preview.info.groups.length}</div>
                  <div className="text-xs text-muted-foreground">Grupos</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <Globe className="h-6 w-6 mx-auto mb-1 text-primary" />
                  <div className="text-2xl font-bold">{preview.info.countries.length || 'N/A'}</div>
                  <div className="text-xs text-muted-foreground">Países</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <File className="h-6 w-6 mx-auto mb-1 text-primary" />
                  <div className="text-2xl font-bold">{formatFileSize(preview.info.fileSize)}</div>
                  <div className="text-xs text-muted-foreground">Tamanho</div>
                </div>
              </div>

              {/* Groups */}
              {preview.info.groups.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Grupos Encontrados:</h4>
                  <div className="flex flex-wrap gap-2">
                    {preview.info.groups.slice(0, 10).map((group, index) => (
                      <Badge key={index} variant="secondary">
                        {group}
                      </Badge>
                    ))}
                    {preview.info.groups.length > 10 && (
                      <Badge variant="outline">
                        +{preview.info.groups.length - 10} mais
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Sample Channels */}
              <div>
                <h4 className="font-medium mb-2">Canais de Exemplo:</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {preview.channels.map((channel, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                      <div>
                        <div className="font-medium">{channel.name}</div>
                        {channel.groupTitle && (
                          <div className="text-xs text-muted-foreground">{channel.groupTitle}</div>
                        )}
                      </div>
                      {channel.tvgLogo && (
                        <img 
                          src={channel.tvgLogo} 
                          alt={channel.name}
                          className="w-8 h-8 rounded object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
